import React from 'react';

import type { Moment } from 'moment';

import {
  SingleLanguageMessageComposer,
  Props as SingleLanguageProps,
} from './single-language-message-composer';

type InputProps = {
  orgId: number;
};

type SingleLanguageFileUploadProps = InputProps &
  Omit<SingleLanguageProps, 'onSendMessage'> & {
    onSendMessage?: (
      message: string,
      messageType: string,
      subject: string,
      sendMessageAt: Moment | null,
      optionalFields?: {
        urlFileMappings?: Record<string, string>;
        sendWithVCard?: boolean;
      },
    ) => Promise<any>;
  };

export type UseFileUploadType = {
  handleUploadClick: (file: File) => Promise<any>;
  handleDeleteFileClick: (urlFileMapping: UrlFileMapping) => () => void;
  urlFileMappings: UrlFileMapping[];
};

// TODO[PROD-3488]: remove component and allow uploads via the canUpload prop
export const SingleLanguageComposerWithPlugins = ({
  ...messageComposerProps
}: SingleLanguageFileUploadProps) => {
  const { onMessageSent, onSendMessage } = messageComposerProps;

  const handleSendMessageWithFileUploads =
    onSendMessage &&
    ((
      message: string,
      messageType: string,
      subject: string,
      sendMessageAt: Moment | null,
      options?: {
        urlFileMappings?: Array<UrlFileMapping>;
        sendWithVCard?: boolean;
      },
    ) => {
      const formattedFileMappings = (options?.urlFileMappings || [])
        .filter(({ isDeleted }) => !isDeleted)
        .reduce((a, v) => ({ ...a, [v.urlKey]: v.downloadableUrl }), {});
      return onSendMessage(message, messageType, subject, sendMessageAt, {
        urlFileMappings: formattedFileMappings,
        sendWithVCard: options?.sendWithVCard,
      });
    });

  // only clear out urlFileMappings if the message was successfully sent. If not, keep the same
  // urlFileMappings so the user can still send the message with urls once they fix their errors
  const handleSuccessfulMessageSent = () => {
    if (onMessageSent) {
      onMessageSent();
    }
  };

  return (
    <SingleLanguageMessageComposer
      {...messageComposerProps}
      onMessageSent={handleSuccessfulMessageSent}
      onSendMessage={handleSendMessageWithFileUploads}
      canTranslate
      canUploadFile
    />
  );
};
